import { Observable } from 'rxjs';
import { HttpRequest, HttpEvent, HttpParams } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { RequestAggregatorMethod } from '@nimbus-global/features/shared/models/api/request-aggregator/request-aggregator-method.enum';
import { IHttpStrategy, IRequestOptions } from '@nimbus-library/features/shared/services/base-http/i-http.strategy';
import { AggregatedRequestScope } from './aggregated-request.scope';

@Injectable({ providedIn: 'root' })
export class AggregatedHttpStrategy implements IHttpStrategy {
  private scope: AggregatedRequestScope;
  private baseUrl: string;

  public constructor(@Inject(DOCUMENT) document: Document) {
    let baseUrl = document.location.origin;
    switch (baseUrl) {
      case 'http://localhost:4200':
        baseUrl = 'https://portal.us-01.stage.nimbdev.com';
        break;
      case 'http://localhost:4201':
        baseUrl = 'https://admin.us-01.stage.nimbdev.com';
        break;
      default:
        break;
    }

    this.baseUrl = baseUrl;
  }

  public beginScope(): void {
    this.scope = new AggregatedRequestScope();
  }
  public endScope(): AggregatedRequestScope {
    const scope = this.scope;
    this.scope = null;

    return scope;
  }

  public hasOpenScope(): boolean {
    return !!this.scope;
  }
  public get(url: string, options: IRequestOptions): Observable<any> {
    return this.scope.addRequest$(
      RequestAggregatorMethod.Get,
      this.getUrlWithParams(url, options),
      undefined,
      this.getCustomHeaders(options),
    );
  }
  public put(url: string, body: string, options: IRequestOptions): Observable<any> {
    return this.scope.addRequest$(RequestAggregatorMethod.Put, this.getUrlWithParams(url, options), body, this.getCustomHeaders(options));
  }
  public patch(url: string, body: string, options: IRequestOptions): Observable<any> {
    return this.scope.addRequest$(RequestAggregatorMethod.Patch, this.getUrlWithParams(url, options), body, this.getCustomHeaders(options));
  }
  public post(url: string, body: string, options: IRequestOptions): Observable<any> {
    return this.scope.addRequest$(RequestAggregatorMethod.Post, this.getUrlWithParams(url, options), body, this.getCustomHeaders(options));
  }
  public delete(url: string, options: IRequestOptions): Observable<any> {
    return this.scope.addRequest$(
      RequestAggregatorMethod.Delete,
      this.getUrlWithParams(url, options),
      undefined,
      this.getCustomHeaders(options),
    );
  }
  public request<R>(req: HttpRequest<any>): Observable<HttpEvent<R>> {
    throw new Error('Method not supported');
  }

  private getCustomHeaders(options: IRequestOptions): Record<string, string> | undefined {
    if (!options || !options.headers) {
      return undefined;
    }

    const result: Record<string, string> = {};
    for (const name of options.headers.keys()) {
      const value = options.headers.get(name);
      if (value !== null && value !== undefined) {
        result[name] = value;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private getUrlWithParams(url: string, options: IRequestOptions): string {
    url = this.baseUrl + url;
    if (!options || !options.params) {
      return url;
    }

    if (options.params instanceof HttpParams) {
      const paramString = (options.params as HttpParams).toString();
      return `${url}${url.indexOf('?') === -1 ? '?' : '&'}${paramString}`;
    }

    const paramNames = Object.keys(options.params);
    if (paramNames.length === 0) {
      return url;
    }

    let result = url;
    result += result.indexOf('?') === -1 ? '?' : '&';
    result += paramNames
      .map((paramName) => {
        const param = options.params[paramName];
        if (Array.isArray(param)) {
          return param.map((option) => `${paramName}=${encodeURIComponent(option)}`).join('&');
        }

        return `${paramName}=${encodeURIComponent(param)}`;
      })
      .join('&');

    return result;
  }
}
